import React, {useState} from 'react';
import {Box, Button, IconButton, Stack, TextField, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useTranslation} from 'react-i18next';

interface Props {
    /** Existing explanation, if the user has rated this message before. */
    initialExplanation?: string;
    /** Submit the (possibly empty) written explanation. */
    onSubmit: (explanation: string) => void;
    /** Dismiss the feedback panel — rating stays saved either way. */
    onClose: () => void;
}

/**
 * Inline feedback panel — appears below an assistant message after the
 * user clicks thumbs-up or thumbs-down. Soft background, closable,
 * with a 3-row textarea for an optional written explanation.
 *
 * Doesn't know which rating it's for; the parent records the rating
 * separately via its own thumb buttons. This box only collects the
 * optional explanation.
 */
const AssistantFeedbackBox: React.FC<Props> = ({
    initialExplanation = '',
    onSubmit,
    onClose,
}) => {
    const {t} = useTranslation();
    const [draft, setDraft] = useState(initialExplanation);

    return (
        <Box
            sx={{
                mt: 1.5,
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'rgba(0, 0, 0, 0.035)',
                border: '1px solid rgba(0, 0, 0, 0.08)',
            }}
        >
            <Stack direction="row" alignItems="flex-start" sx={{mb: 1}}>
                <Stack sx={{flex: 1, minWidth: 0}} spacing={0.25}>
                    <Typography sx={{fontSize: 14, fontWeight: 600}}>
                        {t('Thanks for your rating')}
                    </Typography>
                    <Typography
                        sx={{fontSize: 13}}
                        color="text.secondary"
                    >
                        {t('Why did you choose this rating?')}{' '}
                        <Box component="span" sx={{opacity: 0.7}}>
                            {t('(optional)')}
                        </Box>
                    </Typography>
                </Stack>
                <IconButton
                    size="small"
                    onClick={onClose}
                    aria-label={t('Close feedback')}
                >
                    <CloseIcon sx={{fontSize: 18}}/>
                </IconButton>
            </Stack>

            <TextField
                multiline
                rows={3}
                fullWidth
                value={draft}
                onChange={e => setDraft(e.target.value)}
                placeholder={t('Provide additional feedback')}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        bgcolor: '#fff',
                        fontSize: 14,
                    },
                    '& .MuiInputBase-input': {
                        fontSize: 14,
                        lineHeight: 1.45,
                    },
                }}
            />

            <Stack direction="row" justifyContent="flex-end" sx={{mt: 1}}>
                <Button
                    size="small"
                    variant="contained"
                    disabled={draft.trim() === (initialExplanation || '').trim()}
                    onClick={() => onSubmit(draft.trim())}
                    sx={{textTransform: 'none', fontSize: 13}}
                >
                    {t('Submit')}
                </Button>
            </Stack>
        </Box>
    );
};

export default AssistantFeedbackBox;
